import { useCallback, useMemo, useState } from 'react';

import { useEntities } from './useEntities.js';

/** `All` pill: not an integration id, so it is kept out of the constants list. */
export const ALL_INTEGRATIONS = 'all';

/**
 * Draft state for the entity picker modal (CT-5192).
 *
 * **One fetch, then local narrowing — deliberately.** The inline picker asks the
 * service for its shortlist; this modal loads the whole catalogue once and lets
 * the source pills narrow it in memory. `All sources` has to render everything
 * anyway, so re-fetching on each pill click would buy nothing and cost a
 * round trip per click. This is the sanctioned exception to the "filter in the
 * payload" rule, not an oversight — please do not "fix" it.
 *
 * The selection is a **draft**: nothing reaches `useWidgetBuilderForm` until the
 * caller commits, so `Cancel` and `Esc` genuinely discard.
 *
 * @param {{ committedEntityRef?: string, isOpen?: boolean }} options
 */
export const useEntityPicker = ({ committedEntityRef = '', isOpen = false } = {}) => {
  const { entities, isLoading, error, refetch } = useEntities();

  const [draft, setDraft] = useState({ ref: committedEntityRef, session: isOpen });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIntegration, setActiveIntegration] = useState(ALL_INTEGRATIONS);

  // Re-seeding is derived from the open flag rather than written in an effect:
  // a new session means the draft is whatever is committed, so reopening after a
  // cancel shows the committed entity and not the discarded draft.
  const draftEntityRef = draft.session === isOpen ? draft.ref : committedEntityRef;

  const setDraftEntityRef = useCallback(
    (ref) => setDraft({ ref, session: isOpen }),
    [isOpen],
  );

  const resetDraft = useCallback(() => {
    setDraft({ ref: committedEntityRef, session: isOpen });
    setSearchTerm('');
    setActiveIntegration(ALL_INTEGRATIONS);
  }, [committedEntityRef, isOpen]);

  const visibleEntities = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    return entities.filter((entity) => {
      if (activeIntegration !== ALL_INTEGRATIONS && entity.integration !== activeIntegration) {
        return false;
      }
      if (!needle) return true;
      // Name, integration **and** description: the cards show all three, so a
      // user typing a word they can see should find the row.
      return (
        entity.name.toLowerCase().includes(needle)
        || entity.integration.toLowerCase().includes(needle)
        || entity.description.toLowerCase().includes(needle)
      );
    });
  }, [activeIntegration, entities, searchTerm]);

  return useMemo(
    () => ({
      entities,
      visibleEntities,
      draftEntityRef,
      searchTerm,
      activeIntegration,
      canUse: Boolean(draftEntityRef),
      isLoading,
      error,
      refetch,
      setDraftEntityRef,
      setSearchTerm,
      setActiveIntegration,
      resetDraft,
    }),
    [
      activeIntegration,
      draftEntityRef,
      entities,
      error,
      isLoading,
      refetch,
      resetDraft,
      searchTerm,
      setDraftEntityRef,
      visibleEntities,
    ],
  );
};

export default useEntityPicker;
